import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';

import { ActionType } from '../../model/constant';
import { AppConfigService } from '../../services/app-config/app-config.service';
import { Billing } from '../../model/billing/billing.model';
import { BillingFormComponent } from './billing-form/billing-form.component';
import { DialogConfirmActionComponent } from '../modal/dialog-confirm-action/dialog-confirm-action.component';
import { DialogConfirmComponent } from 'app/components/modal/dialog-confirm/dialog-confirm.component';
import { DialogGeneralMessageComponent } from 'app/components/modal/dialog-general-message/dialog-general-message.component';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Rx';
import { UserService } from 'app/services/user/user.service';
import { UtilitiesService } from '../../services/utilities/utilities.service';
import { DialogConfirmType } from '../modal/dialog-confirm/dialog-confirm.model';

@Component({
  selector: 'billing-address',
  templateUrl: './billing-address.component.html',
  styleUrls: ['./billing-address.component.scss']
})
export class BillingAddressComponent implements OnInit, OnChanges, OnDestroy {
  private readonly LIMIT_ADDING = 5; // tslint:disable-line
  private readonly STATE = {
    VIEW: 'VIEW', EDIT: 'EDIT', ADD: 'ADD', DELETE: 'DELETE',
  };
  private defaultOptions = { page: 'checkout3', selectable: true, editable: true, name: 'billingAddress' };
  private currentState = this.STATE.VIEW;
  private emptyBilling = new Billing(); // tslint:disable-line
  private subscriptionBilling: Subscription;

  @Input() options = this.defaultOptions;
  @Input('items') dataItems = []; // tslint:disable-line:no-input-rename
  @Output() onFetchingData: EventEmitter<any> = new EventEmitter();
  @Output() onSelectedData: EventEmitter<any> = new EventEmitter();
  @ViewChild('addForm') componentAddForm: BillingFormComponent;
  @ViewChild('editForm') componentEditForm: BillingFormComponent;


  constructor(
    private userService: UserService,
    private appConfigService: AppConfigService,
    private dialog: MdDialog,
  ) { }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges) {
    changes.options && changes.options.currentValue && this.onOptionsChanging(changes.options.currentValue);

    const dataItemsHaveValue = changes.dataItems && changes.dataItems.currentValue;
    if (dataItemsHaveValue && changes.dataItems.currentValue.length) {
      this.onDataItemsChanging(changes.dataItems.currentValue);
    } else {
      this.currentState = this.STATE.ADD;
      this.onSelectedData.emit(this.getSelectedItem());
    }
  }

  ngOnDestroy() {
    this.subscriptionBilling && this.subscriptionBilling.unsubscribe();
  }

  getCurrentFormComponent() {
    return this.currentState === this.STATE.ADD ? this.componentAddForm : this.componentEditForm;
  }

  getCurrentForm() {
    const currentFormComponent = this.getCurrentFormComponent();
    return currentFormComponent && currentFormComponent.getFormControl();
  }

  autoSubmitBillingForm$() {
    if (!this.getCurrentFormComponent())  {
      return Observable.of(false);
    }
    return this.getCurrentFormComponent().setSubmitting$();
  }

  setToViewMode() {
    this.currentState !== this.STATE.VIEW && this.clearSelectedState();
  }

  validateSubmitingForm(allowNotification = true) {
    let available;
    const currentFormComponent = this.getCurrentFormComponent();
    if (!currentFormComponent) {
      return true;
    }

    const currentForm = this.getCurrentForm();
    switch (this.currentState) {
      case this.STATE.EDIT:
        allowNotification && this.notifyConfirmFormCancel();
        available = (currentForm.valid && !allowNotification);
        break;

      case this.STATE.ADD:
        const formChanged = currentFormComponent.isFormChanged();
        allowNotification && formChanged && this.notifyConfirmFormCancel();
        available = (currentForm.valid && !formChanged && !allowNotification);
        available && this.setDefaultState();
        break;

      default:
        available = true;
        break;
    }
    return available;
  }

  isViewMode() {
    return this.currentState === this.STATE.VIEW;
  }

  getSelectedItem() {
    const [selectedItem] = this.dataItems.filter(item => item.isSelected);
    return selectedItem;
  }

  private onOptionsChanging(options) {
    this.options = {...this.defaultOptions, ...options };
  }

  private onDataItemsChanging(dataItems) {
    this.setDefaultState();
    this.ensureItemDefault();
    this.onSelectedData.emit(this.getSelectedItem());
  }

  private ensureItemDefault() {
    const [defaultItem] = this.dataItems.filter(item => item.isDefault);
    !defaultItem && this.dataItems.length && (this.dataItems[0].isDefault = this.dataItems[0].isSelected = true);
  }

  private clearSelectedState(excludeDefaultSelected = false) {
    this.currentState = this.STATE.VIEW;
    const [itemChecked] = this.dataItems.filter(item => item.isChecked);
    this.dataItems = this.dataItems.map(item => {
      const isChecked = itemChecked ? item.isChecked : item.isDefault;
      return {
        ...item,
        state: this.STATE.VIEW,
        isChecked: excludeDefaultSelected ? false : isChecked,
        isSelected: excludeDefaultSelected ? false : isChecked,
      };
    });
  }

  private setDefaultState() {
    this.currentState = this.STATE.VIEW;
    this.dataItems = this.dataItems.map((item) => ({
      ...item,
      state: this.STATE.VIEW,
      isChecked: item.isDefault,
      isSelected: item.isDefault,
    }));
  }

  /**
   ***************** UI Event Handler ******************************
   */
  // tslint:disable:no-unused-variable
  private onItemSelected(newSelected = this.getSelectedItem()) {
    this.dataItems = this.dataItems.map(item => {
      const isMatchId = newSelected.billingId === item.billingId;
      return { ...item,
        isChecked: isMatchId,
        isSelected: this.isViewMode() ? isMatchId : item.isSelected,
      };
    });
    this.onSelectedData.emit(newSelected);
  }

  private onAddButtonClicked(data) {
    if (this.currentState === this.STATE.EDIT) {
      this.notifyConfirmFormCancel();
    } else {
      this.clearSelectedState(true);
      this.currentState = this.STATE.ADD;
    }
  }

  private onEditButtonClicked(data) {
    if (this.currentState === this.STATE.EDIT) {
      this.notifyConfirmFormCancel();
    } else {
      this.clearSelectedState(true);
      this.currentState = this.STATE.EDIT;
      this.dataItems = this.dataItems.map(item => {
        const isMatchId = data.billingId === item.billingId;
        return { ...item,
          isChecked: isMatchId,
          isSelected: isMatchId,
          state: isMatchId ? this.STATE.EDIT : this.STATE.VIEW
        };
      });
      this.onItemSelected();
    }
  }

  private onDeleteButtonClicked(data) {
    if (this.currentState === this.STATE.EDIT) {
      this.notifyConfirmFormCancel();
    } else {
      // show confirm delete
      const title = this.appConfigService.getTranslate('checkout-3-billing-confirm-delete-msg');
      const mainBtn = this.appConfigService.getTranslate('checkout-3-billing-dialog-delete-button');
      const config = {
        data: { mainBtn }
      };
      const dialogRef: MdDialogRef<DialogConfirmActionComponent> = this.dialog.open(DialogConfirmActionComponent, config);
      dialogRef.componentInstance.dialogRef = dialogRef;
      dialogRef.componentInstance.message = title;

      // on confirm delete is ok
      const confirmButtonClicked$ = dialogRef.componentInstance.onConfirmClicked();
      this.subscriptionBilling = confirmButtonClicked$
      .flatMap(() => {
        return this.userService.deleteBillingAddress(data);
      })
      .subscribe((res) => {
        dialogRef.close();
        res && this.onDeleteCompleted();
      }, this.onDeleteFailure.bind(this));
    }
  }

  private onDeleteCompleted() {
    const title = this.appConfigService.getTranslate('checkout-3-billing-delete-success');
    setTimeout(() => {
      this.notifyMessage(title);
    }, 500);
    this.onFetchingData.emit();
  }

  private onDeleteFailure() {
    const title = this.appConfigService.getTranslate('checkout-3-billing-delete-failure');
    const message = this.appConfigService.getTranslate('checkout-3-billing-failure-text-try-again');
    this.notifyMessage(title, message, 'icon-service-failed')
    .afterClosed().subscribe(() => {
      this.setDefaultState();
    });
  }

  private notifyMessage(title: string, message = '', icon = 'icon-successful', mainBtn = undefined): MdDialogRef<DialogGeneralMessageComponent> {
    mainBtn = mainBtn || this.appConfigService.getTranslate('btn-ok');
    const config = {
      data: { icon, title, message, mainBtn }
    };
    return this.dialog.open(DialogGeneralMessageComponent, config);
  }

  private notifyConfirmFormCancel() {
    const dialogRef: MdDialogRef<DialogConfirmComponent> = this.dialog.open(DialogConfirmComponent,  { data: { confirmType: DialogConfirmType.CONFIRM_CLOSE } });
    dialogRef.afterClosed().subscribe((res) => {
      res && this.onFormCancelCompleted();
    });
  }

  private onFormSaveCompleted(data) {
    const { billing, state } = data || { billing: undefined, state: ActionType.normalSave };
    this.setDefaultState();
    (state === ActionType.autoSave) ? this.onSelectedData.emit(billing) : this.onFetchingData.emit();
  }

  private onFormCancelCompleted() {
    this.clearSelectedState();
  }

}



// WEBPACK FOOTER //
// ./src/app/components/billing-address/billing-address.component.ts